/** Modo simulação: um cenário no servidor.
 *
 * Ligar cria (ou reaproveita) um cenário; os lançamentos feitos com ele recebem
 * `scenario_id` e entram na projeção tracejados. Descartar apaga o cenário inteiro —
 * é o "reverter sem dificuldade". Aplicar torna os lançamentos reais sem re-lançar.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import * as api from "./api";

interface SimulationValue {
  active: boolean;
  scenarioId: string | null;
  count: number;
  /** Diferença que os itens simulados fazem no saldo de fim do período. */
  delta: number;
  setDelta: (v: number) => void;
  start: () => Promise<string>;
  /** Garante um cenário e devolve o id, para usar ao lançar. */
  ensure: () => Promise<string>;
  discard: () => Promise<void>;
  apply: () => Promise<void>;
}

const Ctx = createContext<SimulationValue | null>(null);

export function SimulationProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [active, setActive] = useState(false);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [delta, setDelta] = useState(0);

  const { data: scenarios } = useQuery({
    queryKey: ["scenarios"],
    queryFn: api.getScenarios,
    enabled: active,
  });

  const count = useMemo(
    () => scenarios?.find((s) => s.id === scenarioId)?.items ?? 0,
    [scenarios, scenarioId],
  );

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["projection"] });
    qc.invalidateQueries({ queryKey: ["transactions"] });
    qc.invalidateQueries({ queryKey: ["scenarios"] });
  }, [qc]);

  const ensure = useCallback(async () => {
    if (scenarioId) return scenarioId;
    const s = await api.createScenario(`Simulação de ${new Date().toLocaleDateString("pt-BR")}`);
    setScenarioId(s.id);
    setActive(true);
    return s.id;
  }, [scenarioId]);

  const start = useCallback(async () => {
    const id = await ensure();
    setActive(true);
    invalidate();
    return id;
  }, [ensure, invalidate]);

  const discard = useMutation({
    mutationFn: async () => {
      if (scenarioId) await api.deleteScenario(scenarioId);
    },
    onSuccess: () => {
      setScenarioId(null);
      setActive(false);
      setDelta(0);
      invalidate();
    },
  });

  const apply = useMutation({
    mutationFn: async () => {
      if (scenarioId) await api.applyScenario(scenarioId);
    },
    onSuccess: () => {
      setScenarioId(null);
      setActive(false);
      setDelta(0);
      invalidate();
    },
  });

  const value: SimulationValue = {
    active,
    scenarioId,
    count,
    delta,
    setDelta,
    start,
    ensure,
    discard: async () => {
      await discard.mutateAsync();
    },
    apply: async () => {
      await apply.mutateAsync();
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSimulation() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSimulation fora do SimulationProvider");
  return v;
}
